import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: Reset Password
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  const styles = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '40px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '400px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '8px',
      color: '#333'
    },
    subtitle: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '24px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '6px',
      color: '#555'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box',
      fontFamily: 'inherit'
    },
    button: {
      width: '100%',
      padding: '10px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      marginTop: '16px',
      transition: 'background-color 0.2s'
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#007bff',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '16px',
      fontWeight: '500'
    },
    messageBox: {
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '16px',
      fontSize: '14px'
    },
    successMessage: {
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb'
    },
    errorMessage: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f5c6cb'
    },
    infoMessage: {
      backgroundColor: '#d1ecf1',
      color: '#0c5460',
      border: '1px solid #bee5eb'
    },
    link: {
      color: '#007bff',
      cursor: 'pointer',
      textDecoration: 'none',
      marginTop: '16px',
      display: 'block',
      textAlign: 'center'
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    console.log('entered verify email');

    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/verifyemail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Email verified! Now set your new password.' });
        setStep(2);
      } else {
        // Email doesn't exist
        alert(data.message || 'This email does not exist in our admin user list. Please contact your super admin to add you as a member.');
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/resetpassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to reset password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setEmail('');
    setNewPassword('');
    setConfirmPassword('');
    setMessage({ type: '', text: '' });
    
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {step > 1 && (
          <button style={styles.backButton} onClick={handleBack}>
            ← Back
          </button>
        )}

        {/* Step 1: Email Verification */}
        {step === 1 && (
          <div>
            <h2 style={styles.title}>Forgot Password?</h2>
            <p style={styles.subtitle}>Enter your email address to reset your password</p>

            {message.text && (
              <div
                style={{
                  ...styles.messageBox,
                  ...(message.type === 'error' ? styles.errorMessage : styles.infoMessage)
                }}
              >
                {message.text}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                style={styles.input}
                placeholder="Enter your admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <button
              style={{
                ...styles.button,
                ...(loading && styles.buttonDisabled)
              }}
              onClick={handleVerifyEmail}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Continue'}
            </button>

            <a href="/" style={styles.link}>
              Back to Login
            </a>
          </div>
        )}

        {/* Step 2: Reset Password */}
        {step === 2 && (
          <div>
            <h2 style={styles.title}>Reset Password</h2>
            <p style={styles.subtitle}>Create a new password for your admin account</p>

            {message.text && (
              <div
                style={{
                  ...styles.messageBox,
                  ...(message.type === 'error' ? styles.errorMessage : styles.successMessage)
                }}
              >
                {message.text}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <input
                type="password"
                style={styles.input}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                Minimum 8 characters
              </small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                style={styles.input}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button
              style={{
                ...styles.button,
                ...(loading && styles.buttonDisabled)
              }}
              onClick={handleResetPassword}
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;